import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ModalClass, YesNoModalDialogComponent, YesNoModalDialogModel } from '@evorait/pace-core';
import { finalize, take, tap } from 'rxjs/operators';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { PhysicalInventoryComponent } from '../components/physical-inventory.component';
import { PhysicalInventoryModel } from '../models/physical-inventory.model';

@Injectable({ providedIn: 'root' })
export class PhysicalInventoryModalService {
    private isPhysicalInventoryModalOpen = false;
    private alertModalRef?: BsModalRef;

    public constructor(
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
    ) {}

    public openPIAlert(alertMessage = '', isAlertConfirmed = false, futureAlert = false): Observable<boolean> {
        if (this.alertModalRef) {
            this.alertModalRef.hide();
        }

        const confirmText = <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_CONFIRM_LABEL');
        const remindLaterText = <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_REMIND_LATER_LABEL');
        const okayText = <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_OKAY_LABEL');

        this.alertModalRef = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_TITLE'),
                    alertMessage,
                    isAlertConfirmed ? okayText : confirmText,
                    isAlertConfirmed || futureAlert ? '' : remindLaterText,
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return (this.alertModalRef?.content?.modalResult ?? of(false)).pipe(
            take(1),
            finalize(() => {
                this.alertModalRef?.hide();
                this.alertModalRef = undefined;
            }),
        );
    }

    public openPhysicalInventoryModal(piModel: PhysicalInventoryModel): void {
        if (this.isPhysicalInventoryModalOpen) {
            return;
        }
        this.isPhysicalInventoryModalOpen = true;
        const modalInstance = this.modalService.show(PhysicalInventoryComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_FULLSCREEN,
            initialState: {
                model: piModel,
            },
            ariaLabelledBy: 'physical-inventory-modal-title',
        });

        modalInstance.onHidden
            ?.pipe(
                take(1),
                tap(() => {
                    this.isPhysicalInventoryModalOpen = false;
                }),
            )
            .subscribe();
    }

    public getOfflineAlertMessage(startTime: string, endTime: string): string {
        return <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_TODAY_MESSAGE', {
            startTime: startTime,
            endTime: endTime,
            message1: <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_OFFLINE_MESSAGE'),
            message2: <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_COMMON_MESSAGE'),
        });
    }

    public getScheduleAlertMessageForToday(startTime: string, endTime: string): string {
        return <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_TODAY_MESSAGE', {
            startTime: startTime,
            endTime: endTime,
            message1: <string>this.translate.instant('PHYSICAL_INVENTORY_NEW_SCHEDULE_ALERT_COMPLETE_MESSAGE'),
            message2: <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_COMMON_MESSAGE'),
        });
    }

    public getScheduleAlertMessageForTomorrow(startTime: string, endTime: string): string {
        return <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_TOMORROW_MESSAGE', {
            startTime: startTime,
            endTime: endTime,
            message1: <string>this.translate.instant('PHYSICAL_INVENTORY_NEW_SCHEDULE_ALERT_COMPLETE_MESSAGE'),
            message2: <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_COMMON_MESSAGE'),
        });
    }

    public getScheduleAlertMessageForFutureDate(date: string, startTime: string, endTime: string): string {
        return <string>this.translate.instant('PHYSICAL_INVENTORY_NEW_SCHEDULE_ALERT_MESSAGE', {
            date: date,
            startTime: startTime,
            endTime: endTime,
            message1: <string>this.translate.instant('PHYSICAL_INVENTORY_NEW_SCHEDULE_ALERT_COMPLETE_MESSAGE'),
            message2: <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_COMMON_MESSAGE'),
        });
    }

    public getRescheduleAlertMessage(): string {
        return <string>this.translate.instant('PHYSICAL_INVENTORY_RESCHEDULED_MESSAGE', {
            message1: <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_COMMON_MESSAGE'),
        });
    }
}
